// Imperative toast shown top-right on desktop, top-center on mobile.
// Used by inline save flows (e.g. auto-save) where pulling in the React
// ToastProvider context would be awkward.
//
// Two-step save flow (per `feedback_save_toast_pattern.md`):
//   const handle = showToast(t`Saving…`, "loading");
//   try { ...; handle.update(t`Saved`, "success"); }
//   catch { handle.update(t`Save failed`, "error"); }
//
// The `loading` variant does NOT auto-dismiss; calling `update()` on the
// returned handle re-arms the auto-dismiss timer so the success/error
// state disappears after the standard window.

type ToastVariant = "success" | "error" | "info" | "loading";

export interface ToastHandle {
  dismiss(): void;
  update(message: string, variant: ToastVariant): void;
}

const CONTAINER_ID = "pontimed-toast-container";

const BORDER_BY_VARIANT: Record<ToastVariant, string> = {
  success: "border-emerald-200 dark:border-emerald-800",
  error: "border-red-200 dark:border-red-800",
  info: "border-blue-200 dark:border-blue-800",
  loading: "border-border",
};

const ICON_BY_VARIANT: Record<ToastVariant, string> = {
  success:
    '<svg viewBox="0 0 24 24" class="size-4 shrink-0 text-emerald-500" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg>',
  error:
    '<svg viewBox="0 0 24 24" class="size-4 shrink-0 text-red-500" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M12 9v4"/><path d="M12 17h.01"/><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/></svg>',
  info: '<svg viewBox="0 0 24 24" class="size-4 shrink-0 text-blue-500" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><path d="M12 16v-4"/><path d="M12 8h.01"/></svg>',
  loading:
    '<svg viewBox="0 0 24 24" class="size-4 shrink-0 text-muted-foreground animate-spin" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M21 12a9 9 0 1 1-6.219-8.56"/></svg>',
};

// IMPORTANT: BASE_CLASS must NOT include opacity/translate state — those are
// applied imperatively (HIDDEN_CLASSES) only at fade-in start and fade-out end.
// Otherwise a `renderInto()` call (e.g. from handle.update()) would reset the
// element back to the hidden state and the success toast never becomes visible.
const BASE_CLASS =
  "pointer-events-auto flex w-full max-w-sm items-center gap-3 rounded-xl border bg-card px-4 py-3 text-sm font-medium text-foreground shadow-lg transition-all duration-200 ease-out sm:w-auto sm:min-w-[16rem] ";

const HIDDEN_CLASSES = ["opacity-0", "-translate-y-2"] as const;

const AUTO_DISMISS_MS = 6000;
const REMOVE_TRANSITION_MS = 220;

// Single-slot policy: a new toast replaces any existing ones immediately so
// successive saves never stack (and never drift downward as old fade-outs hold
// flex slots). Each handle registers a killer here keyed by its element.
const TOAST_KILLERS = new WeakMap<HTMLElement, () => void>();

function evictExistingToasts(container: HTMLElement): void {
  const existing = Array.from(container.children) as HTMLElement[];
  for (const el of existing) {
    const kill = TOAST_KILLERS.get(el);
    kill?.();
    TOAST_KILLERS.delete(el);
    // Pin the evicted toast to its current visual rect and pull it out of the
    // flex flow. Otherwise the 220ms fade-out keeps holding a flex slot, and
    // the freshly-spawned toast renders BELOW the invisible old one, then
    // "climbs" up when the old one is finally removed. The user sees it as
    // two distinct toasts.
    const rect = el.getBoundingClientRect();
    el.style.position = "fixed";
    el.style.top = `${rect.top}px`;
    el.style.left = `${rect.left}px`;
    el.style.width = `${rect.width}px`;
    el.style.pointerEvents = "none";
    el.classList.add(...HIDDEN_CLASSES);
    setTimeout(() => el.remove(), REMOVE_TRANSITION_MS);
  }
}

function ensureContainer(): HTMLElement {
  if (typeof document === "undefined") return null as unknown as HTMLElement;
  let el = document.getElementById(CONTAINER_ID);
  if (el) return el;
  el = document.createElement("div");
  el.id = CONTAINER_ID;
  // Top-center on mobile, top-right on sm+.
  // Stays below any fixed header via top offset; uses safe-area-inset-top for notched devices.
  el.className =
    "pointer-events-none fixed inset-x-0 top-[calc(env(safe-area-inset-top)+0.75rem)] z-[9999] flex flex-col items-center gap-2 px-4 sm:inset-auto sm:right-4 sm:top-4 sm:items-end sm:px-0";
  document.body.appendChild(el);
  return el;
}

function renderInto(toast: HTMLElement, message: string, variant: ToastVariant): void {
  toast.className = BASE_CLASS + BORDER_BY_VARIANT[variant];
  toast.innerHTML = `${ICON_BY_VARIANT[variant]}<span class="flex-1">${escapeHtml(message)}</span>`;
}

export function showToast(message: string, variant: ToastVariant = "success"): ToastHandle {
  if (typeof document === "undefined") {
    return { dismiss: () => {}, update: () => {} };
  }
  const container = ensureContainer();
  if (!container) {
    return { dismiss: () => {}, update: () => {} };
  }

  // Latest-wins: kick out any existing toast(s) so a fresh save never
  // stacks below an in-flight fade-out.
  evictExistingToasts(container);

  const toast = document.createElement("div");
  renderInto(toast, message, variant);
  // Start hidden, then fade in next frame.
  toast.classList.add(...HIDDEN_CLASSES);
  container.appendChild(toast);

  requestAnimationFrame(() => {
    toast.classList.remove(...HIDDEN_CLASSES);
  });

  let dismissTimer: ReturnType<typeof setTimeout> | null = null;
  let removeTimer: ReturnType<typeof setTimeout> | null = null;
  let removed = false;

  const armDismiss = () => {
    if (dismissTimer) clearTimeout(dismissTimer);
    dismissTimer = setTimeout(() => {
      if (removed) return;
      toast.classList.add(...HIDDEN_CLASSES);
      removeTimer = setTimeout(() => {
        if (removed) return;
        removed = true;
        TOAST_KILLERS.delete(toast);
        toast.remove();
      }, REMOVE_TRANSITION_MS);
    }, AUTO_DISMISS_MS);
  };

  // External killer: lets evictExistingToasts() short-circuit this handle's
  // pending timers when a fresh toast pre-empts it.
  TOAST_KILLERS.set(toast, () => {
    removed = true;
    if (dismissTimer) clearTimeout(dismissTimer);
    if (removeTimer) clearTimeout(removeTimer);
  });

  // Loading toasts persist until updated or dismissed; everything else
  // auto-dismisses after the standard window.
  if (variant !== "loading") armDismiss();

  return {
    dismiss() {
      if (removed) return;
      if (dismissTimer) clearTimeout(dismissTimer);
      toast.classList.add(...HIDDEN_CLASSES);
      removeTimer = setTimeout(() => {
        if (removed) return;
        removed = true;
        TOAST_KILLERS.delete(toast);
        toast.remove();
      }, REMOVE_TRANSITION_MS);
    },
    update(nextMessage: string, nextVariant: ToastVariant) {
      if (removed) return;
      renderInto(toast, nextMessage, nextVariant);
      if (nextVariant === "loading") {
        if (dismissTimer) clearTimeout(dismissTimer);
      } else {
        armDismiss();
      }
    },
  };
}

/**
 * Wrap a save operation with the saving → saved/error toast lifecycle.
 *
 *   await withSavingToast(() => apiFetch.put("/api/x", body), {
 *     saving: t`Saving…`,
 *     saved: t`Saved`,
 *     error: t`Could not save changes`,
 *   });
 *
 * The `saving` toast appears immediately; on resolve it becomes `saved`,
 * on reject it becomes `error`. The original error is re-thrown so callers
 * can still react to failures.
 */
export async function withSavingToast<T>(
  task: () => Promise<T>,
  messages: { saving: string; saved: string; error: string },
): Promise<T> {
  const handle = showToast(messages.saving, "loading");
  try {
    const result = await task();
    handle.update(messages.saved, "success");
    return result;
  } catch (err) {
    handle.update(messages.error, "error");
    throw err;
  }
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}
